import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { PropertyDefinition } from '@peruselab/api-client/assertions';
import type { MoleculeFacts, MoleculeSummary } from '@peruselab/api-client/molecules';
import { Icon } from '../../components/Icon';
import { conditionTexts, formatValue } from '../chemistry/molecule/assertionText';
import { ghsWording, statementKey } from '../chemistry/molecule/factsheetText';
import { LearnMore, OtherSources } from '../chemistry/molecule/PublishedFacts';
import { StatementSection } from '../chemistry/molecule/StatementForm';
import { MonographSafety } from './MonographSafety';
import { statementsFor, type MoleculePropertiesState } from '../chemistry/molecule/useMoleculeProperties';
import { usePublicAccess } from '../public/publicAccess';
import { MonographHero } from './MonographHero';
import { MonographSmiles } from './MonographSmiles';
import { PropertyEvidence } from './PropertyEvidence';
import { agreedProperty, propertyCards, type PropertyCard } from './propertyCards';
import { UnusualChargeNote } from '../chemistry/molecule/UnusualChargeNote';
import { SaltLinks } from '../chemistry/molecule/SaltLinks';
import { StructureCheckLine } from '../chemistry/molecule/StructureCheckLine';

type Tab = 'overview' | 'properties' | 'safety' | 'uses' | 'sources';

/** What the full Properties list needs from the Cards view: which property to show, and "add a value". */
export interface PropertiesListRequest {
  readonly focusPropertyId: string | null;
  /** #931: the property the evidence view shows, and the reader's display unit for it. */
  readonly propertyId: string;
  readonly displayUnit: string | null;
  readonly onAdded: (propertyId: string) => void;
  readonly addRequest: number;
}

interface MonographCardsProps {
  readonly summary: MoleculeSummary;
  readonly facts: MoleculeFacts | null;
  readonly name: string | null;
  readonly formula: ReactNode;
  readonly weight: string | null;
  /** No database knows this structure: say so (principle 4). */
  readonly noPublished: boolean;
  readonly properties: MoleculePropertiesState;
  /** A comment's anchor was tapped (or the address names one): open the tab it's on. */
  readonly focusAssertionId: string | null;
  readonly focusStatementKey: string | null;
  /** One property's values (PropertiesSection, layout "evidence"), inside the evidence view (#931). */
  readonly renderPropertiesList: (request: PropertiesListRequest) => ReactNode;
  readonly discussion: ReactNode;
}

function PropertyCardButton({
  card,
  catalog,
  onOpen
}: {
  readonly card: PropertyCard;
  /** The whole catalog: a condition's unit (°C for a pressure's temperature) is another property's. */
  readonly catalog: readonly PropertyDefinition[];
  readonly onOpen: () => void;
}) {
  const { t } = useTranslation();
  if (!card.preferred)
    return (
      <button type="button" className="monograph-property-card" onClick={onOpen}>
        <span className="monograph-property-card-name">{card.property.name}</span>
        <span className="monograph-property-card-detail">{t('properties.report.noPreferred')}</span>
      </button>
    );
  const { quantities, solvent } = conditionTexts(card.preferred, catalog);
  const conditions = [
    ...(quantities.length > 0 ? [t('properties.at.quantity', { value: quantities.join(', ') })] : []),
    ...(solvent ? [`${t('properties.in')} ${solvent}`] : [])
  ].join(' ');
  const detail = [card.preferred.method.detail, conditions, card.source].filter(Boolean).join(' · ');
  const method = card.preferred.method.kind;
  const value = formatValue(card.preferred, card.property);
  return (
    <button type="button" className="monograph-property-card" onClick={onOpen}>
      <span className="monograph-property-card-name">{card.property.name}</span>
      <span className="monograph-property-card-value" dir="ltr">
        {value}
      </span>
      {detail ? <span className="monograph-property-card-detail">{detail}</span> : null}
      <span className="monograph-property-card-chips">
        {method === 'CALCULATED' || method === 'PREDICTED' ? (
          <span className="monograph-chip monograph-chip--warn">{t(`monograph.method.${method}`)}</span>
        ) : card.sourced > 0 ? (
          <span className="monograph-chip">{t('monograph.sourced', { count: card.sourced })}</span>
        ) : (
          <span className="monograph-chip monograph-chip--plain">
            {t(`properties.standing.${card.preferred.standing}`)}
          </span>
        )}
        {card.agreement === 'agree' ? (
          <span className="monograph-card-note">{t('monograph.allAgree')}</span>
        ) : card.agreement === 'differ' ? (
          <span className="monograph-card-note">{t('monograph.values', { count: card.count })}</span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * #929 (epic #934): the Monograph's default view -- direction **B · Overview with cards** on the
 * design canvas. A hero with the structure and identifiers; tabs; at a glance; a card per property
 * showing its preferred value (ADR 0018 D9) and how it stands; About; and the discussion alongside.
 */
export function MonographCards({
  summary,
  facts,
  name,
  formula,
  weight,
  noPublished,
  properties,
  focusAssertionId,
  focusStatementKey,
  renderPropertiesList,
  discussion
}: MonographCardsProps) {
  const { t, i18n } = useTranslation();
  const { signedIn, signIn } = usePublicAccess();
  const location = useLocation();
  const tabsId = useId();
  // #930: an address ending in #properties opens the Properties tab (every value) directly.
  const [tab, setTab] = useState<Tab>(() => (globalThis.location?.hash === '#properties' ? 'properties' : 'overview'));
  const [focusPropertyId, setFocusPropertyId] = useState<string | null>(null);
  const [addRequest, setAddRequest] = useState(0);
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  const ready = facts?.status === 'ready' ? facts : null;
  // #947: statements are assertions once imported; PubChem's cached text until then.
  const about = statementsFor(properties.loaded, 'description', ready?.about);
  const uses = statementsFor(properties.loaded, 'use', ready?.uses);
  const toxicity = statementsFor(properties.loaded, 'toxicity-summary', ready?.toxicity);

  // A value a comment is about lives on the Properties tab; a statement on Overview, Uses or Safety.
  const focusedValueProperty = properties.loaded?.assertions.find(
    (assertion) => assertion.id === focusAssertionId
  )?.propertyId;
  // #947: a statement a comment is about lives on Overview (About), Uses or Safety.
  const focusedStatement = properties.loaded?.statements.find((statement) => statement.id === focusAssertionId);
  useEffect(() => {
    if (!focusAssertionId) return;
    if (focusedStatement) {
      setTab(
        focusedStatement.propertyId === 'use'
          ? 'uses'
          : focusedStatement.propertyId === 'toxicity-summary'
            ? 'safety'
            : 'overview'
      );
      return;
    }
    setTab('properties');
    if (focusedValueProperty) setFocusPropertyId(focusedValueProperty);
  }, [focusAssertionId, focusedValueProperty, focusedStatement]);
  useEffect(() => {
    if (!focusStatementKey || !ready) return;
    const isIn = (statements: readonly { source: string; text: string }[] | null | undefined) =>
      (statements ?? []).some((statement) => statementKey(statement.source, statement.text) === focusStatementKey);
    if (isIn(uses)) setTab('uses');
    else if (isIn(toxicity)) setTab('safety');
    else if (isIn(about)) setTab('overview');
  }, [focusStatementKey, ready]);
  const cards = properties.loaded ? propertyCards(properties.loaded) : [];
  const agreed = agreedProperty(cards);
  const hazard = (ready?.ghs ?? []).find((classification) => classification.signal);
  const cid = summary.pubchemCid ? Number(summary.pubchemCid) : null;

  const tabs: readonly { readonly id: Tab; readonly label: string }[] = [
    { id: 'overview', label: t('monograph.tabs.overview') },
    {
      id: 'properties',
      label:
        cards.length > 0 ? t('monograph.tabs.propertiesCount', { count: cards.length }) : t('monograph.tabs.properties')
    },
    // #947 phase 2: Safety and Uses are always there -- the first use or toxicity note can be added.
    { id: 'safety' as const, label: t('monograph.tabs.safety') },
    { id: 'uses' as const, label: t('monograph.tabs.uses') },
    ...(ready ? [{ id: 'sources' as const, label: t('monograph.tabs.sources') }] : [])
  ];
  const current = tabs.some((candidate) => candidate.id === tab) ? tab : 'overview';

  const showProperty = (propertyId: string | null) => {
    setTab('properties');
    setFocusPropertyId(propertyId);
  };
  const addValue = () => {
    if (!signedIn) {
      signIn(location.pathname + location.search);
      return;
    }
    setTab('properties');
    setAddRequest((request) => request + 1);
  };
  // WAI-ARIA tabs: arrows move between tabs, Home/End to the ends.
  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + tabs.length) % tabs.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const target = tabs[next]!.id;
    setTab(target);
    tabRefs.current[target]?.focus();
  };
  const hazardWording = hazard?.hazards[0]
    ? ghsWording(hazard.hazards[0].code, (part) => {
        const key = `ghs.hazard.${part}`;
        return i18n.exists(key) ? t(key) : undefined;
      })
    : null;

  return (
    <div className="monograph-cards">
      <MonographHero
        summary={summary}
        facts={facts}
        about={about}
        name={name}
        formula={formula}
        weight={weight}
        onAddValue={addValue}
        smiles={summary.smiles ? <MonographSmiles smiles={summary.smiles} haptoBonds={summary.haptoBonds} /> : null}
      />

      <div className="monograph-tabs" role="tablist" aria-label={t('monograph.tabs.label')}>
        {tabs.map((candidate, index) => (
          <button
            key={candidate.id}
            ref={(element) => {
              tabRefs.current[candidate.id] = element;
            }}
            type="button"
            role="tab"
            id={`${tabsId}-tab-${candidate.id}`}
            aria-selected={current === candidate.id}
            aria-controls={`${tabsId}-panel`}
            tabIndex={current === candidate.id ? 0 : -1}
            className={current === candidate.id ? 'active' : ''}
            onClick={() => {
              setTab(candidate.id);
              if (candidate.id === 'properties') setFocusPropertyId(null);
            }}
            onKeyDown={(event) => onTabKey(event, index)}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      <div className="monograph-body">
        <div
          className="monograph-main"
          role="tabpanel"
          id={`${tabsId}-panel`}
          aria-labelledby={`${tabsId}-tab-${current}`}
        >
          {current === 'overview' ? (
            <>
              {agreed || hazard ? (
                <section className="monograph-glance" aria-labelledby={`${tabsId}-glance`}>
                  <h2 id={`${tabsId}-glance`}>{t('monograph.glance.heading')}</h2>
                  <div className="monograph-glance-cards">
                    {agreed ? (
                      <div className="monograph-glance-card">
                        <p>{t('monograph.glance.agree')}</p>
                        <p className="monograph-glance-value">
                          {agreed.property.name}{' '}
                          <span dir="ltr">{formatValue(agreed.preferred!, agreed.property)}</span>
                        </p>
                      </div>
                    ) : null}
                    {hazard?.signal ? (
                      <div className="monograph-glance-card monograph-glance-card--warn">
                        <p>{t('monograph.glance.hazard')}</p>
                        <p className="monograph-glance-value">
                          {t(`ghs.signal.${hazard.signal}`, { defaultValue: hazard.signal })}
                          {hazardWording ? ` · ${hazardWording}` : ''}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </section>
              ) : null}

              <section className="monograph-properties" aria-labelledby={`${tabsId}-properties`}>
                <div className="monograph-section-heading">
                  <h2 id={`${tabsId}-properties`}>{t('properties.heading')}</h2>
                  {cards.length > 0 ? <p>{t('monograph.propertiesHint')}</p> : null}
                </div>
                {properties.loaded && cards.length === 0 ? (
                  <p className="discussion-note">{t('properties.none')}</p>
                ) : null}
                <div className="monograph-property-grid">
                  {cards.map((card) => (
                    <PropertyCardButton
                      key={card.property.id}
                      card={card}
                      catalog={properties.loaded?.catalog ?? []}
                      onOpen={() => showProperty(card.property.id)}
                    />
                  ))}
                  <button type="button" className="monograph-property-add" onClick={addValue}>
                    <Icon name={signedIn ? 'add' : 'lock'} size="1.4rem" />
                    <span>{signedIn ? t('properties.add') : t('properties.signInToAdd')}</span>
                  </button>
                </div>
              </section>

              <StructureCheckLine moleculeId={summary.id} />
              <SaltLinks moleculeId={summary.id} />
              <UnusualChargeNote facts={facts} />
              {about.length > 0 ? null : facts?.status === 'pending' ? (
                <p className="published-facts-status" role="status">
                  {t('moleculePage.factsLoading')}
                </p>
              ) : noPublished ? (
                // Principle 4: honest about gaps -- the page is still whole, and says why it's short.
                <p className="molecule-factsheet-gap">
                  <Icon name="info" size="1rem" />
                  <span>{t('moleculePage.noPublished')}</span>
                </p>
              ) : null}
              <StatementSection
                moleculeId={summary.id}
                propertyId="description"
                title={t('moleculePage.about')}
                statements={about}
                shownFirst={1}
                onChanged={() => void properties.reload()}
              />
            </>
          ) : null}

          {current === 'properties' ? (
            <PropertyEvidence
              name={name}
              properties={properties}
              focusPropertyId={focusPropertyId}
              renderValues={({ propertyId, displayUnit, onAdded }) =>
                renderPropertiesList({ focusPropertyId, addRequest, propertyId, displayUnit, onAdded })
              }
            />
          ) : null}
          {current === 'safety' ? (
            <MonographSafety
              moleculeId={summary.id}
              facts={facts}
              toxicity={toxicity}
              onChanged={() => void properties.reload()}
            />
          ) : null}
          {current === 'uses' ? (
            <StatementSection
              moleculeId={summary.id}
              propertyId="use"
              title={t('moleculePage.uses')}
              statements={uses}
              onChanged={() => void properties.reload()}
            />
          ) : null}
          {current === 'sources' && ready ? (
            <>
              <OtherSources sources={ready.otherSources} />
              {cid ? <LearnMore cid={cid} /> : null}
            </>
          ) : null}
        </div>

        <aside className="monograph-discussion">{discussion}</aside>
      </div>
    </div>
  );
}
